import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Modal, Button } from 'semantic-ui-react';

import UsersModal from '../UsersModal';
import InviteUserModal from '../InviteUserModal';

import styles from './UserManagementModal.module.scss';

const UserManagementModal = React.memo(({
  isOpen,
  onClose,
  // Props para UsersModal
  users,
  canAddUsers,
  onUserUpdate,
  onUserUsernameUpdate,
  onUserUsernameUpdateMessageDismiss,
  onUserEmailUpdate,
  onUserEmailUpdateMessageDismiss,
  onUserPasswordUpdate,
  onUserPasswordUpdateMessageDismiss,
  onUserDelete,
}) => {
  const [t] = useTranslation();
  const [activeTab, setActiveTab] = useState('manage'); // 'manage' o 'invite'
  const [inviteSubmitRef, setInviteSubmitRef] = useState(null);

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
  }, []);

  const handleClose = useCallback(() => {
    setActiveTab('manage'); // Reset to default tab
    onClose();
  }, [onClose]);

  const handleInviteSuccess = useCallback(() => {
    // Después de invitar exitosamente, volver a la pestaña de gestión
    setActiveTab('manage');
  }, []);

  const handleInviteCancel = useCallback(() => {
    // Al cancelar invitación, volver a gestión (no cerrar todo)
    setActiveTab('manage');
  }, []);

  if (!isOpen) return null;

  return (
    <Modal open onClose={handleClose} size="large" centered={false}>
      <Modal.Header>
        {t('common.userManagement')}
      </Modal.Header>

      <Modal.Content scrolling className={styles.content}>
        {activeTab === 'manage' ? (
          <div className={styles.manageSection}>
            <UsersModal
              items={users}
              canAdd={canAddUsers}
              onUpdate={onUserUpdate}
              onUsernameUpdate={onUserUsernameUpdate}
              onUsernameUpdateMessageDismiss={onUserUsernameUpdateMessageDismiss}
              onEmailUpdate={onUserEmailUpdate}
              onEmailUpdateMessageDismiss={onUserEmailUpdateMessageDismiss}
              onPasswordUpdate={onUserPasswordUpdate}
              onPasswordUpdateMessageDismiss={onUserPasswordUpdateMessageDismiss}
              onDelete={onUserDelete}
              onClose={() => {}} // No cerrar el modal padre
              isEmbedded={true} // Indicar que está embebido
            />
          </div>
        ) : (
          <div className={styles.inviteSection}>
            <InviteUserModal
              onClose={handleInviteCancel}
              onInviteSuccess={handleInviteSuccess}
              onSetSubmitRef={setInviteSubmitRef}
              isEmbedded={true} // Indicar que está embebido
            />
          </div>
        )}
      </Modal.Content>

      <Modal.Actions>
        {activeTab === 'manage' ? (
          <>
            <Button onClick={() => handleTabChange('invite')} primary>
              {t('action.inviteNewUser')}
            </Button>
            <Button onClick={handleClose}>
              {t('action.close')}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => handleTabChange('manage')}>
              {t('action.cancel')}
            </Button>
            <Button
              primary
              onClick={() => {
                // Ejecutar la función de submit del InviteUserModal
                if (inviteSubmitRef) {
                  inviteSubmitRef();
                }
              }}
            >
              {t('action.sendInvitation')}
            </Button>
          </>
        )}
      </Modal.Actions>
    </Modal>
  );
});

UserManagementModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  users: PropTypes.array.isRequired,
  canAddUsers: PropTypes.bool.isRequired,
  onUserUpdate: PropTypes.func.isRequired,
  onUserUsernameUpdate: PropTypes.func.isRequired,
  onUserUsernameUpdateMessageDismiss: PropTypes.func.isRequired,
  onUserEmailUpdate: PropTypes.func.isRequired,
  onUserEmailUpdateMessageDismiss: PropTypes.func.isRequired,
  onUserPasswordUpdate: PropTypes.func.isRequired,
  onUserPasswordUpdateMessageDismiss: PropTypes.func.isRequired,
  onUserDelete: PropTypes.func.isRequired,
};

export default UserManagementModal;
